import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import {
  FiPlus,
  FiRefreshCw,
  FiX,
  FiAlertCircle,
  FiTrash2,
  FiRotateCcw,
} from 'react-icons/fi';

const TenantWalletManager = ({ tenantId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [, setSelectedTransaction] = useState(null);
  const [adjustForm, setAdjustForm] = useState({
    communicationType: 'email',
    amount: 0,
    description: '',
    reason: ''
  });
  const [refundForm, setRefundForm] = useState({
    transactionId: '',
    reason: ''
  });
  const [clearForm, setClearForm] = useState({
    communicationType: 'all',
    reason: ''
  });
  const [alertConfig, setAlertConfig] = useState({ show: false, title: '', message: '', type: 'info' });

  const triggerAlert = (title, message, type = 'info') => {
    setAlertConfig({ show: true, title, message, type });
  };

  // ✅ FIXED: Wrap fetchWalletData in useCallback to memoize it
  const fetchWalletData = useCallback(async () => {
    try {
      setLoading(true);
      const [walletRes, transactionsRes] = await Promise.all([
        api.get(`/api/super-admin/tenant-wallets/${tenantId}`),
        api.get(`/api/super-admin/tenant-wallets/${tenantId}/transactions?limit=50`)
      ]);
      
      if (walletRes.data.success) {
        setWallet(walletRes.data.data);
      }
      if (transactionsRes.data.success) {
        console.log('📊 Transactions received:', transactionsRes.data.data);
        setTransactions(transactionsRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      triggerAlert('Error', 'Failed to fetch wallet data', 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // ✅ FIXED: Include fetchWalletData in dependency array
  useEffect(() => {
    if (tenantId) {
      fetchWalletData();
    }
  }, [tenantId, fetchWalletData]);

  const handleAdjustCredits = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await api.post(`/api/super-admin/tenant-wallets/${tenantId}/adjust`, {
        communicationType: adjustForm.communicationType,
        amount: parseFloat(adjustForm.amount),
        description: adjustForm.description,
        reason: adjustForm.reason
      });
      
      if (response.data.success) {
        triggerAlert('Success', 'Credits adjusted successfully', 'success');
        setShowAdjustModal(false);
        fetchWalletData();
        setAdjustForm({
          communicationType: 'email',
          amount: 0,
          description: '',
          reason: ''
        });
      }
    } catch (error) {
      triggerAlert('Error', error.response?.data?.message || 'Failed to adjust credits', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRefundTransaction = async (e) => {
    e.preventDefault();
    
    // Validate that a valid transaction ID is selected
    if (!refundForm.transactionId || refundForm.transactionId === '') {
      triggerAlert('Error', 'Please select a valid transaction to refund', 'error');
      return;
    }
    
    console.log('🔄 Refunding transaction ID:', refundForm.transactionId);
    
    setSaving(true);
    try {
      const response = await api.post(`/api/super-admin/tenant-wallets/${tenantId}/refund`, {
        transactionId: refundForm.transactionId,
        reason: refundForm.reason
      });
      
      if (response.data.success) {
        triggerAlert('Success', 'Transaction refunded successfully', 'success');
        setShowRefundModal(false);
        fetchWalletData();
        setRefundForm({ transactionId: '', reason: '' });
        setSelectedTransaction(null);
      }
    } catch (error) {
      console.error('❌ Refund error:', error);
      triggerAlert('Error', error.response?.data?.message || 'Failed to refund transaction', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleClearCredits = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await api.delete(`/api/super-admin/tenant-wallets/${tenantId}/clear`, {
        data: {
          communicationType: clearForm.communicationType,
          reason: clearForm.reason
        }
      });
      
      if (response.data.success) {
        triggerAlert('Success', 'Credits cleared successfully', 'success');
        setShowClearModal(false);
        fetchWalletData();
        setClearForm({ communicationType: 'all', reason: '' });
      }
    } catch (error) {
      triggerAlert('Error', error.response?.data?.message || 'Failed to clear credits', 'error');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAmountColor = (amount) => {
    if (amount > 0) return 'text-emerald-600';
    if (amount < 0) return 'text-rose-600';
    return 'text-slate-400';
  };

  const getStatusBadge = (status) => {
    const colors = {
      completed: 'bg-emerald-100 text-emerald-700',
      pending: 'bg-amber-100 text-amber-700',
      failed: 'bg-rose-100 text-rose-700',
      refunded: 'bg-blue-100 text-blue-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  // Get recharge transactions that can be refunded
  const refundableTransactions = transactions.filter(
    t => t.type === 'recharge' && t.status !== 'refunded' && t.id
  );

  return (
    <div className="space-y-4">
      {/* Wallet Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
          <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Email Credits</p>
          <p className="text-2xl font-black text-blue-900">{wallet?.emailCredits || 0}</p>
          <p className="text-[10px] text-blue-500">Used: {wallet?.emailTotalUsed || 0}</p>
        </div>
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
          <p className="text-[10px] font-bold text-purple-600 uppercase tracking-wider">SMS Credits</p>
          <p className="text-2xl font-black text-purple-900">{wallet?.smsCredits || 0}</p>
          <p className="text-[10px] text-purple-500">Used: {wallet?.smsTotalUsed || 0}</p>
        </div>
        <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg p-4 border border-emerald-200">
          <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Total Sent</p>
          <p className="text-2xl font-black text-emerald-900">{wallet?.totalEmailsSent || 0}</p>
          <p className="text-[10px] text-emerald-500">Emails</p>
        </div>
        <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200">
          <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Total Sent</p>
          <p className="text-2xl font-black text-amber-900">{wallet?.totalSmsSent || 0}</p>
          <p className="text-[10px] text-amber-500">SMS</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setShowAdjustModal(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors"
        >
          <FiPlus className="w-3.5 h-3.5" />
          Adjust Credits
        </button>
        <button
          onClick={() => setShowRefundModal(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 transition-colors"
        >
          <FiRotateCcw className="w-3.5 h-3.5" />
          Refund Transaction
        </button>
        <button
          onClick={() => setShowClearModal(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-rose-600 text-white rounded-lg text-xs font-bold hover:bg-rose-700 transition-colors"
        >
          <FiTrash2 className="w-3.5 h-3.5" />
          Clear Credits
        </button>
        <button
          onClick={fetchWalletData}
          className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold hover:bg-slate-200 transition-colors"
        >
          <FiRefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* Transactions Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
          <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Transaction History</h4>
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50/50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Date</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Type</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Channel</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Amount</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Before</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">After</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Source</th>
                <th className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {transactions.map((t) => (
                <tr key={t.id || t._id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-2 text-slate-500">{formatDate(t.createdAt)}</td>
                  <td className="px-3 py-2 font-bold capitalize">{t.type}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                      t.communicationType === 'email' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'
                    }`}>
                      {t.communicationType}
                    </span>
                  </td>
                  <td className={`px-3 py-2 font-bold ${getAmountColor(t.amount)}`}>
                    {t.amount > 0 ? `+${t.amount}` : t.amount}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{t.creditsBefore}</td>
                  <td className="px-3 py-2 text-slate-600">{t.creditsAfter}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${getStatusBadge(t.status)}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-[10px]">{t.source}</td>
                  <td className="px-3 py-2">
                    {t.type === 'recharge' && t.status !== 'refunded' && (
                      <button
                        onClick={() => {
                          setSelectedTransaction(t);
                          const transactionId = t.id || t._id;
                          setRefundForm({ 
                            transactionId: transactionId, 
                            reason: '' 
                          });
                          setShowRefundModal(true);
                        }}
                        className="p-1 text-emerald-600 hover:text-emerald-700 rounded hover:bg-emerald-50 transition-colors"
                        title="Refund"
                      >
                        <FiRotateCcw className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {transactions.length === 0 && (
                <tr>
                  <td colSpan="9" className="px-3 py-8 text-center text-slate-400">
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Adjust Credits Modal */}
      {showAdjustModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-sm font-bold text-slate-900">Adjust Credits</h3>
              <button onClick={() => setShowAdjustModal(false)} className="p-1 bg-slate-100 rounded-full hover:bg-slate-200">
                <FiX className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <form onSubmit={handleAdjustCredits} className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Communication Type</label>
                <select
                  value={adjustForm.communicationType}
                  onChange={(e) => setAdjustForm({...adjustForm, communicationType: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Amount (+ add, - deduct)</label>
                <input
                  type="number"
                  step="1"
                  required
                  value={adjustForm.amount}
                  onChange={(e) => setAdjustForm({...adjustForm, amount: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., 1000 or -500"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Description</label>
                <input
                  type="text"
                  value={adjustForm.description}
                  onChange={(e) => setAdjustForm({...adjustForm, description: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  placeholder="Reason for adjustment"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Reason (optional)</label>
                <textarea
                  rows="2"
                  value={adjustForm.reason}
                  onChange={(e) => setAdjustForm({...adjustForm, reason: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  placeholder="Additional notes"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAdjustModal(false)}
                  className="w-full py-2.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full py-2.5 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? 'Processing...' : 'Apply Adjustment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Refund Transaction Modal */}
      {showRefundModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-sm font-bold text-slate-900">Refund Transaction</h3>
              <button 
                onClick={() => {
                  setShowRefundModal(false);
                  setRefundForm({ transactionId: '', reason: '' });
                }} 
                className="p-1 bg-slate-100 rounded-full hover:bg-slate-200"
              >
                <FiX className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-xs">
                <p className="text-slate-500 mb-2">Select a transaction to refund:</p>
                {refundableTransactions.length === 0 ? (
                  <p className="text-amber-600 text-xs">No refundable transactions available.</p>
                ) : (
                  <select
                    value={refundForm.transactionId}
                    onChange={(e) => {
                      console.log('✅ Selected transaction ID:', e.target.value);
                      setRefundForm({...refundForm, transactionId: e.target.value});
                    }}
                    className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select a transaction...</option>
                    {refundableTransactions.map((t) => {
                      const transactionId = t.id || t._id;
                      const displayText = `${formatDate(t.createdAt)} - ${t.communicationType} - ${t.amount > 0 ? `+${t.amount}` : t.amount} credits`;
                      return (
                        <option key={transactionId} value={transactionId}>
                          {displayText}
                        </option>
                      );
                    })}
                  </select>
                )}
                {refundForm.transactionId && (
                  <div className="mt-2 p-2 bg-blue-50 rounded text-blue-700 text-[10px]">
                    <span className="font-bold">Selected Transaction ID:</span>{' '}
                    <span className="font-mono text-xs">{refundForm.transactionId}</span>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Reason</label>
                <textarea
                  rows="2"
                  value={refundForm.reason}
                  onChange={(e) => setRefundForm({...refundForm, reason: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  placeholder="Reason for refund"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowRefundModal(false);
                    setRefundForm({ transactionId: '', reason: '' });
                  }}
                  className="w-full py-2.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRefundTransaction}
                  disabled={saving || !refundForm.transactionId || refundForm.transactionId === ''}
                  className="w-full py-2.5 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 disabled:opacity-50"
                >
                  {saving ? 'Processing...' : 'Refund Credits'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clear Credits Modal */}
      {showClearModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-sm font-bold text-slate-900">Clear Credits</h3>
              <button onClick={() => setShowClearModal(false)} className="p-1 bg-slate-100 rounded-full hover:bg-slate-200">
                <FiX className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700">
                <FiAlertCircle className="inline w-4 h-4 mr-1" />
                Warning: This action will permanently remove credits from the tenant's wallet.
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Communication Type</label>
                <select
                  value={clearForm.communicationType}
                  onChange={(e) => setClearForm({...clearForm, communicationType: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All Credits</option>
                  <option value="email">Email Only</option>
                  <option value="sms">SMS Only</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Reason</label>
                <textarea
                  rows="2"
                  value={clearForm.reason}
                  onChange={(e) => setClearForm({...clearForm, reason: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  placeholder="Reason for clearing credits"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowClearModal(false)}
                  className="w-full py-2.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearCredits}
                  disabled={saving}
                  className="w-full py-2.5 bg-rose-600 text-white rounded-lg text-xs font-bold hover:bg-rose-700 disabled:opacity-50"
                >
                  {saving ? 'Processing...' : 'Clear Credits'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {alertConfig.show && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-3 text-center shadow-xl">
            <div className={`p-3 rounded-full w-fit mx-auto ${
              alertConfig.type === 'error' ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'
            }`}>
              <FiAlertCircle className="w-6 h-6" />
            </div>
            <h3 className="text-sm font-bold text-slate-900">{alertConfig.title}</h3>
            <p className="text-xs text-slate-500">{alertConfig.message}</p>
            <button 
              onClick={() => setAlertConfig(prev => ({...prev, show: false}))} 
              className="w-full py-2.5 bg-slate-900 text-white font-bold text-xs rounded-xl hover:bg-slate-800"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantWalletManager;